import { useState } from 'react';
import { Shield, Clock, Save, X, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

export const InterviewWelcomeBanner = () => {
  const [isDismissed, setIsDismissed] = useState(false);

  const handleDismiss = () => {
    setIsDismissed(true);
  };

  if (isDismissed) return null;

  return (
    <Card className="mb-6 border-primary/20 bg-primary/5 shadow-lg">
      <CardContent className="p-6 relative">
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-4 right-4 h-8 w-8 text-muted-foreground hover:text-foreground"
          onClick={handleDismiss}
        >
          <X className="h-4 w-4" />
        </Button>

        <div className="space-y-4 pr-8">
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-2 flex items-center gap-2">
              <CheckCircle className="h-6 w-6 text-primary" />
              Welcome to Your Emergency Plan Interview
            </h2>
            <p className="text-base text-foreground/80">
              This personalized interview creates a comprehensive emergency plan tailored specifically to your home, family, and location.
            </p>
          </div>

          <Accordion type="multiple" className="w-full">
            <AccordionItem value="privacy" className="border-primary/20">
              <AccordionTrigger className="hover:no-underline py-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Shield className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground text-left">Your Privacy is Protected</h3>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pl-13 pr-4 text-sm text-muted-foreground">
                <p className="mb-2">
                  All data is encrypted and securely stored using industry-standard protocols. Information is only used to generate your emergency plan and complies with privacy regulations.
                </p>
                <p className="mb-2">
                  <strong className="text-foreground">Personal Identifiable Information (PII) Protection:</strong> Your sensitive personal details are scrubbed and never sent to any AI language models. PII is stored encrypted in our secure database and kept completely separate from AI processing.
                </p>
                <p>
                  <strong className="text-foreground">Your Data, Your Control:</strong> You can request complete erasure of your data at any time through your account settings.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="time" className="border-primary/20">
              <AccordionTrigger className="hover:no-underline py-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="h-5 w-5 text-secondary" />
                  </div>
                  <h3 className="font-semibold text-foreground text-left">Quick & Easy</h3>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pl-13 pr-4 text-sm text-muted-foreground">
                <p className="mb-2">
                  Takes approximately 10-15 minutes to complete.
                </p>
                <p className="mb-2">
                  18 questions covering your household composition, location, medical needs, pets, and specific situation.
                </p>
                <p>
                  You can skip questions if you prefer, though more detail results in a more comprehensive emergency plan.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="save" className="border-b-0">
              <AccordionTrigger className="hover:no-underline py-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-accent/50 flex items-center justify-center flex-shrink-0">
                    <Save className="h-5 w-5 text-accent-foreground" />
                  </div>
                  <h3 className="font-semibold text-foreground text-left">Save Anytime</h3>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pl-13 pr-4 text-sm text-muted-foreground">
                <p className="mb-2">
                  Your progress is automatically saved after each answer.
                </p>
                <p className="mb-2">
                  Exit the interview at any time using the "Save & Exit" button in the header.
                </p>
                <p>
                  Return whenever you like to continue exactly where you left off. Your interview will remain available until completion.
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <div className="flex gap-3 pt-2">
            <Button onClick={handleDismiss} className="flex-1 sm:flex-none">
              Get Started
            </Button>
            <Button onClick={handleDismiss} variant="outline" className="flex-1 sm:flex-none">
              Dismiss
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
